import { type Node, type Edge, Position, MarkerType } from '@xyflow/react';
import { type FlowData, type ActionData } from '../types/interaction-log-data';

const NODE_WIDTH = 320;
const NODE_HEIGHT = 120;
const HORIZONTAL_SPACING = 150;
const VERTICAL_SPACING = 200;

const getLayoutedElements = (nodes: Node[], edges: Edge[]) => {
  const nodePositions = new Map<string, { x: number; y: number }>();
  const processedNodes = new Set<string>();

  const nodesByLevel = new Map<number, string[]>();

  const calculateLevel = (nodeId: string, level: number = 0): void => {
    if (processedNodes.has(nodeId)) return;

    processedNodes.add(nodeId);

    if (!nodesByLevel.has(level)) {
      nodesByLevel.set(level, []);
    }
    nodesByLevel.get(level)!.push(nodeId);

    const outgoingEdges = edges.filter((edge) => edge.source === nodeId);
    outgoingEdges.forEach((edge) => {
      calculateLevel(edge.target, level + 1);
    });
  };

  if (nodes.length > 0) {
    calculateLevel(nodes[0].id);
  }

  nodesByLevel.forEach((nodeIds, level) => {
    const x = level * (NODE_WIDTH + HORIZONTAL_SPACING) + 100;
    const levelHeight = (nodeIds.length - 1) * (NODE_HEIGHT + VERTICAL_SPACING) + NODE_HEIGHT;
    const startY = levelHeight;

    nodeIds.forEach((nodeId, index) => {
      const y = startY + index * (NODE_HEIGHT + VERTICAL_SPACING);
      nodePositions.set(nodeId, { x, y });
    });
  });

  const layoutedNodes = nodes.map((node) => ({
    ...node,
    position: nodePositions.get(node.id) ?? { x: 0, y: 0 }
  }));

  return { nodes: layoutedNodes, edges };
};

interface FlowModuleGroup {
  moduleName: string;
  actions: ActionData[];
  duration: number;
  isFlowModule: true;
}

const createCombinedActions = (
  mainFlow: FlowData,
  allFlows: FlowData[]
): { actions: ActionData[]; flowModuleGroups: FlowModuleGroup[] } => {
  const regularActions = mainFlow.actions.filter((a: ActionData) => a.endTime);
  const flowModuleGroups: FlowModuleGroup[] = [];

  // Find FlowModules that started during this main flow
  const flowModules = allFlows.filter((flow) => flow.resourceType === 'FlowModule');

  flowModules.forEach((flowModule: FlowData) => {
    // Check if FlowModule started during the main flow's execution
    const startedDuringMainFlow =
      flowModule.startTime >= mainFlow.startTime && flowModule.startTime <= (mainFlow.endTime || Date.now());

    if (startedDuringMainFlow) {
      const moduleActions = flowModule.actions.filter((a: ActionData) => a.endTime);
      if (moduleActions.length > 0) {
        flowModuleGroups.push({
          moduleName: flowModule.name,
          actions: moduleActions,
          duration: flowModule.duration || 0,
          isFlowModule: true
        });
      }
    }
  });

  return { actions: regularActions, flowModuleGroups };
};

export const fromProcessedInteractionLogs = (processedFlows: FlowData[]): { nodes: Node[]; edges: Edge[] } => {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Separate main flows and whisper flows first to calculate spacing
  const mainFlows = processedFlows.filter(
    (flow: FlowData) => !flow.type.includes('WHISPER') && flow.resourceType !== 'FlowModule'
  );
  const whisperFlows = processedFlows.filter((flow: FlowData) => flow.type.includes('WHISPER'));

  if (processedFlows.length === 0) {
    return { nodes: [], edges: [] };
  }

  // Start node
  nodes.push({
    id: 'start',
    type: 'start',
    selectable: true,
    draggable: true,
    position: {
      x: 1 * (100 + HORIZONTAL_SPACING) + 100,
      y: 100 / 2
    },
    data: { label: 'Contact Initiated' },
    sourcePosition: Position.Right
  });

  let previousNodeId = 'start';
  let previousFlowOutcome: string | undefined;

  // Process main flows
  mainFlows.forEach((flow: FlowData) => {
    const nodeId = flow.id;

    // Combine main flow actions with FlowModule actions
    const { actions, flowModuleGroups } = createCombinedActions(flow, processedFlows);

    // Create main flow node
    nodes.push({
      id: nodeId,
      type: 'flow',
      position: { x: 100, y: 100 },
      data: {
        label: flow.name,
        duration: flow.duration || 0,
        actions: actions,
        flowModuleGroups: flowModuleGroups,
        outcome: flow.outcome,
        type: flow.type,
        resourceType: flow.resourceType
      },
      selectable: true,
      draggable: true,
      sourcePosition: Position.Right,
      targetPosition: Position.Left
    });

    // Connect to previous node - use the previous flow's outcome for the edge
    const hasOutcome = previousFlowOutcome && previousFlowOutcome !== 'ENDED_FLOW_EXECUTION';
    edges.push({
      id: `${previousNodeId}-${nodeId}`,
      source: previousNodeId,
      target: nodeId,
      animated: true,
      type: hasOutcome ? 'outcome' : 'default',
      style: { strokeWidth: 2 },
      markerEnd: { type: MarkerType.Arrow },
      ...(hasOutcome && { data: { outcome: previousFlowOutcome } })
    });

    previousNodeId = nodeId;
    previousFlowOutcome = flow.outcome;
  });

  // Handle whisper flows if they exist
  if (whisperFlows.length > 0) {
    // Whisper flows in parallel
    whisperFlows.forEach((flow: FlowData) => {
      const nodeId = flow.id;

      nodes.push({
        id: nodeId,
        type: 'flow',
        position: { x: 0, y: 0 },
        data: {
          label: flow.name,
          duration: flow.duration || 0,
          actions: flow.actions.filter((a: ActionData) => a.endTime),
          type: flow.type,
          resourceType: flow.resourceType
        },
        selectable: true,
        draggable: true,
        sourcePosition: Position.Right,
        targetPosition: Position.Left
      });

      edges.push({
        id: `${previousNodeId}-${nodeId}`,
        source: previousNodeId,
        target: nodeId,
        type: 'default',
        animated: true,
        style: { strokeWidth: 2 },
        markerEnd: { type: MarkerType.Arrow }
      });
    });

    // Call Connected node
    const callConnectedId = 'call-connected';
    nodes.push({
      id: callConnectedId,
      type: 'end',
      selectable: true,
      draggable: true,
      position: { x: 0, y: 0 },
      data: { label: 'Call Connected' },
      targetPosition: Position.Left
    });

    // Connect all whisper flows to call connected
    whisperFlows.forEach((flow: FlowData) => {
      edges.push({
        id: `${flow.id}-${callConnectedId}`,
        source: flow.id,
        target: callConnectedId,
        type: 'default',
        animated: true,
        style: { strokeWidth: 2 },
        markerEnd: { type: MarkerType.Arrow }
      });
    });
  }

  // Add end node if no whisper flows exist
  if (whisperFlows.length === 0 && mainFlows.length > 0) {
    const endNodeId = 'end';
    nodes.push({
      id: endNodeId,
      type: 'end',
      position: { x: 0, y: 0 },
      data: { label: 'Flow Completed' },
      targetPosition: Position.Left
    });

    // Connect the last main flow to the end node
    edges.push({
      id: `${previousNodeId}-${endNodeId}`,
      source: previousNodeId,
      target: endNodeId,
      type: 'default',
      animated: true,
      style: { strokeWidth: 2 },
      markerEnd: { type: MarkerType.Arrow }
    });
  }

  // Apply Dagre layout to all nodes and edges
  return getLayoutedElements(nodes, edges);
};
